import { useParams, Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import SEO, { getArticleSchema, getBreadcrumbSchema } from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const articleContentMap: Record<string, string> = {
  'xin-shou-ma-ma-jiao-lv-zhi-nan': `
    <p>成为妈妈，是人生中最深刻的身份转变之一。然而，伴随着新生命带来的喜悦，许多新手妈妈也经历着前所未有的焦虑——担心自己做得不够好、害怕宝宝出任何问题、因为睡眠不足而情绪崩溃……这些感受，你并不孤单。</p>

    <h2>一、理解新手妈妈的焦虑来源</h2>
    <p>焦虑并非你的错。研究表明，产后焦虑的发生率甚至高于产后抑郁，约有 15%-20% 的新妈妈会经历不同程度的焦虑症状。焦虑的来源是多方面的：</p>
    <ul>
      <li><strong>角色转变的压力：</strong>从独立的个体突然变成 24 小时待命的照顾者，这种转变本身就极具挑战性。</li>
      <li><strong>信息过载：</strong>社交媒体上铺天盖地的「完美妈妈」形象，让你不自觉地陷入比较和自责。</li>
      <li><strong>睡眠剥夺：</strong>持续中断的睡眠严重影响情绪调节能力。</li>
      <li><strong>激素变化：</strong>产后激素水平的剧烈波动直接影响大脑的情绪中枢。</li>
    </ul>

    <h2>二、6 个实用的心理调适练习</h2>

    <h3>练习 1：呼吸锚定法（每天 3 次，每次 2 分钟）</h3>
    <p>当焦虑袭来时，找一个安静的地方坐下。闭上眼睛，将注意力集中在呼吸上。吸气时默数 4 秒，屏住 4 秒，呼气 6 秒。这个简单的练习能迅速激活副交感神经系统，让你从「战斗-逃跑」模式切换到放松状态。</p>

    <h3>练习 2：「够了」清单</h3>
    <p>准备一个笔记本，每天晚上写下 3 件「今天我做得足够好的事情」。哪怕是「我给宝宝喂饱了奶」或「我温柔地回应了宝宝的哭声」。这个练习帮助你重新建立对自己的信任感。</p>

    <h3>练习 3：设定合理的期望值</h3>
    <p>育儿没有标准答案。给自己设定一个具体的、可实现的「今天目标」，而不是追求虚高的完美主义。比如：「今天的目标是让宝宝和我都吃饱穿暖」，而不是「我要成为最棒的妈妈」。</p>

    <h3>练习 4：建立你的支持系统</h3>
    <p>列出一张「我可以求助的人」名单——伴侣、妈妈、闺蜜、邻居……然后真的去求助。告诉伴侣你需要他/她具体做什么，而不是等待对方「主动发现」。明确的沟通比期待默契更有效。</p>

    <h3>练习 5：信息节食</h3>
    <p>每天设定一个「放下手机」的时间段，至少 1 小时。关闭育儿 APP 的通知，暂停刷社交媒体。用这段时间做一件完全与育儿无关的事——读书、听音乐、泡杯茶，或者什么都不做。</p>

    <h3>练习 6：寻求专业帮助的勇气</h3>
    <p>如果焦虑持续超过两周，且严重影响到你的日常生活和亲子互动，请勇敢地寻求专业心理咨询师的帮助。这不是软弱的表现，恰恰相反——求助是最有力量的行为之一。</p>

    <h2>三、写给新手妈妈的最后一句话</h2>
    <p>亲爱的妈妈，请记住：你不需要成为完美的妈妈，你只需要成为「足够好」的妈妈。你的宝宝需要的不是一个从不犯错的超人，而是一个真实的、温暖的、愿意学习和成长的妈妈。在这条路上，我们都在一起。</p>
  `,
  'chan-hou-yi-yue-zi-ke-xue-fang-an': `
    <p>产后 42 天，在现代医学中被称为「产褥期」，传统称为「坐月子」。这是女性身体从妊娠状态恢复到孕前水平的关键时期。然而，许多传统月子习俗缺乏科学依据，甚至可能对健康造成伤害。本文为你梳理一份基于循证医学的产褥期恢复方案。</p>

    <h2>第一阶段：产后第 1-7 天（重点：休息与观察）</h2>
    <p>这一阶段的核心任务是充分休息和关注身体恢复信号。自然分娩的妈妈需要关注会阴伤口愈合，剖宫产的妈妈则需要关注腹部切口的护理。无论哪种分娩方式，产后 24 小时内都应尽早下床活动，这有助于预防血栓形成。</p>

    <h2>第二阶段：产后第 8-21 天（重点：营养与情绪）</h2>
    <p>身体进入快速恢复期。饮食上不需要大补特补，而是要注重蛋白质、铁质和维生素的均衡摄入。每天保证 2000ml 以上的饮水量有助于乳汁分泌。同时，关注情绪变化——产后情绪低落通常在这一阶段达到高峰，如果情绪持续低落超过两周，建议就医咨询。</p>

    <h2>第三阶段：产后第 22-42 天（重点：逐步恢复活动）</h2>
    <p>可以逐步增加活动量，开始进行温和的产后恢复运动，如凯格尔运动、腹式呼吸和轻柔的瑜伽。但应避免剧烈运动和提重物。42 天产检时，医生会评估子宫、盆底肌和腹直肌的恢复情况，并根据结果给出个性化的恢复建议。</p>

    <p>科学坐月子的核心是：充分休息、均衡营养、适度活动、关注情绪。告别那些让你不舒服的传统习俗，用科学的方式好好爱自己。</p>
  `,
  'yu-er-zhong-de-fu-qi-guan-xi': `
    <p>宝宝出生后，许多夫妻会发现彼此之间的距离在不知不觉中拉大了。从「二人世界」到「三口之家」的转变，对每对夫妻来说都是一场考验。研究表明，约 67% 的夫妻在宝宝出生后的第一年报告关系满意度下降。但好消息是，这并非不可避免。</p>

    <h2>为什么有了孩子之后夫妻关系容易降温？</h2>
    <p>原因其实很简单：精力和时间被重新分配了。宝宝的到来将原本属于夫妻二人的时间和注意力大量转移，加上睡眠不足和激素变化带来的情绪波动，矛盾和疏远几乎是必然的。</p>

    <h2>5 个守护夫妻关系的实用策略</h2>

    <h3>策略 1：每天 15 分钟「无孩子对话」</h3>
    <p>等宝宝睡着后，留出 15 分钟只聊彼此——今天发生了什么有趣的事、你最近在想什么、你感觉怎么样……话题完全围绕你们两个人，不谈宝宝。</p>

    <h3>策略 2：分工明确，减少抱怨</h3>
    <p>模糊的期待是矛盾的温床。坐下来认真讨论育儿和家务的分工，写下来并贴在冰箱上。当分工清晰了，「你为什么不做……」这类抱怨会大大减少。</p>

    <h3>策略 3：保持身体接触</h3>
    <p>即使因为产后恢复或疲惫暂时无法恢复性生活，也不要放弃身体接触。一个拥抱、一次牵手、睡前 5 分钟的依偎……这些小动作能持续分泌催产素，维系你们之间的情感纽带。</p>

    <h3>策略 4：定期「约会之夜」</h3>
    <p>哪怕只是把宝宝交给长辈照看 2 个小时，出去喝杯咖啡、散个步。重点是——只有你们两个。这种定期「逃离」让你们有机会重新以伴侣而非父母的身份相处。</p>

    <h3>策略 5：学会在育儿中合作而非竞争</h3>
    <p>育儿不是谁做得多谁做得少的比赛。当爸爸用自己的方式给宝宝换尿布时，即使不够「标准」，也请不要纠正。尊重彼此的育儿风格，把伴侣视为队友而非下属。</p>

    <p>请记住：幸福的家庭，始于幸福的夫妻关系。照顾好你们的关系，就是给宝宝最好的礼物。</p>
  `,
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug],
  );

  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : undefined),
    [article],
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.category === article.category && a.slug !== article.slug)
            .slice(0, 3)
        : [],
    [article],
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] pt-20 px-4">
          <h2 className="font-heading text-xl text-foreground-800 mb-3">文章未找到</h2>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const articleContent =
    articleContentMap[article.slug] ||
    `<p>${article.excerpt}</p><p>完整文章内容即将上线，敬请期待。</p>`;

  const articleSchema = getArticleSchema({
    title: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    url: `https://youngmom.readdy.ai/article/${article.slug}`,
  });

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: '首页', url: 'https://youngmom.readdy.ai/' },
    {
      name: category?.name || article.category,
      url: `https://youngmom.readdy.ai/category/${article.category}`,
    },
    {
      name: article.title,
      url: `https://youngmom.readdy.ai/article/${article.slug}`,
    },
  ]);

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://youngmom.readdy.ai/article/${slug}`}
        ogType="article"
        schema={[articleSchema, breadcrumbSchema]}
      />
      <Navbar />

      <main className="pt-24 pb-16 md:pb-20 bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                {
                  label: category?.name || article.category,
                  href: `/category/${article.category}`,
                },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                {category?.name || article.category}
              </Link>
              <span className="text-sm text-foreground-500 flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                {article.publishedAt}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-background-200 text-foreground-600 text-xs whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          {/* Cover Image */}
          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none mb-12
              prose-headings:font-heading prose-headings:text-foreground-900
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
              prose-strong:text-foreground-900
              prose-li:text-foreground-700 prose-li:leading-relaxed
              prose-ul:my-5 prose-ol:my-5
              [&_p]:text-[15px] md:[&_p]:text-base"
            dangerouslySetInnerHTML={{ __html: articleContent }}
          />

          {/* Share */}
          <div className="flex items-center gap-3 py-6 border-t border-b border-background-200/70 mb-12">
            <span className="text-sm text-foreground-500">分享这篇文章：</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap" aria-label="复制链接">
              <i className="ri-link text-base"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微信">
              <i className="ri-wechat-line text-base"></i>
            </button>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section>
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => {
                  const raCat = categories.find((c) => c.slug === ra.category);
                  return (
                    <ArticleCard
                      key={ra.slug}
                      slug={ra.slug}
                      title={ra.title}
                      excerpt={ra.excerpt}
                      coverImage={ra.coverImage}
                      category={ra.category}
                      categoryName={raCat?.name || ra.category}
                      readTime={ra.readTime}
                      publishedAt={ra.publishedAt}
                    />
                  );
                })}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}